// New feature-vector-backed catalogue health surface. Reads
// /catalogue-health/rows for a scored, prioritized view + /catalogue-health/asin
// for the consolidated per-product drawer. Renders alongside (above) the
// legacy list so the seller can compare — both stay live until the FE cutover.
//
// PRD Block 1/4 shape: state pill + overlapping health flags + fix queue.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import AuthContext from '../../../../context/AuthContext';
import { getCatalogueRows, getAsinDetail } from '../services/catalogueHealthApi';
import styles from './EnrichedRowsPanel.module.css';

const SORT_OPTIONS = [
  { value: 'score_asc',           label: 'Score (low → high)' },
  { value: 'score_desc',          label: 'Score (high → low)' },
  { value: 'suppressed_first',    label: 'Suppressed first' },
  { value: 'mac_missing_desc',    label: 'Most attrs missing' },
  { value: 'revenue_at_risk_desc', label: 'Revenue at risk' },
];

const FILTER_OPTIONS = [
  { value: 'all',          label: 'All' },
  { value: 'suppressed',   label: 'Suppressed only' },
  { value: 'active',       label: 'Active only' },
  { value: 'at_risk',      label: 'At risk' },
  { value: 'with_missing', label: 'With missing attrs' },
];

const FULFILLMENT_OPTIONS = [
  { value: 'all', label: 'All channels' },
  { value: 'fba', label: 'FBA only' },
  { value: 'fbm', label: 'FBM only' },
];

const SUPPRESSION_STATE_META = {
  active:               { label: 'Active',                cls: styles.stateActive },
  search_suppressed:    { label: 'Search suppressed',     cls: styles.stateSearchSuppressed },
  listing_suppressed:   { label: 'Listing suppressed',    cls: styles.stateSuppressed },
  attribute_suppressed: { label: 'Attribute suppressed',  cls: styles.stateAttrSuppressed },
  catalog_item_removed: { label: 'Removed from catalog',  cls: styles.stateSuppressed },
  unknown:              { label: 'Unknown',               cls: styles.stateUnknown },
};

const HEALTH_FLAG_LABEL = {
  has_warnings: 'Warnings',
  low_score:    'Low score',
  buy_box_lost: 'Buy Box lost',
};

// Score bar class picker — thresholds match backend scoreListing ranges.
const scoreBarClass = (score) => {
  if (score == null) return styles.scoreBarUnknown;
  if (score >= 80) return styles.scoreBarHigh;
  if (score >= 50) return styles.scoreBarMid;
  return styles.scoreBarLow;
};

const StatePill = ({ state }) => {
  const meta = SUPPRESSION_STATE_META[state] || SUPPRESSION_STATE_META.unknown;
  return <span className={`${styles.statePill} ${meta.cls}`}>{meta.label}</span>;
};

const HealthFlags = ({ flags }) => {
  if (!flags?.length) return <span className={styles.muted}>—</span>;
  return (
    <div className={styles.flagsRow}>
      {flags.map((f) => (
        <span key={f} className={styles.flagChip}>{HEALTH_FLAG_LABEL[f] || f}</span>
      ))}
    </div>
  );
};

// #71 Format an ISO UTC timestamp as IST (Asia/Kolkata). Used for the
// data-age banner + drawer captured_at.
const fmtIST = (isoUtc) => {
  if (!isoUtc) return null;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(isoUtc)) + ' IST';
  } catch {
    return isoUtc;
  }
};

// #10/#72 Banner strip above the summary. Renders one of:
//   syncing / empty / failed / revoked / stale
// Suppressed on ready + fresh data.
const DataStateBanner = ({ summary }) => {
  const state = summary?.data_state;
  const conn = summary?.connection_state;
  const lastSynced = summary?.last_synced_at;

  if (conn === 'revoked') {
    return (
      <div className={`${styles.banner} ${styles.bannerError}`}>
        Amazon connection was revoked. Fixes are disabled until you reconnect.
      </div>
    );
  }
  if (state === 'never_synced') {
    return (
      <div className={`${styles.banner} ${styles.bannerInfo}`}>
        No catalogue health data yet. Hit Refresh to pull from Amazon.
      </div>
    );
  }
  if (state === 'syncing') {
    return (
      <div className={`${styles.banner} ${styles.bannerInfo}`}>
        Syncing from Amazon — this usually takes 2–5 minutes. Try Refresh in a moment.
      </div>
    );
  }
  if (state === 'empty') {
    return (
      <div className={`${styles.banner} ${styles.bannerWarn}`}>
        Sync completed but returned zero listings. Check that your Amazon store has active SKUs.
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className={`${styles.banner} ${styles.bannerError}`}>
        Sync completed with a state-count discrepancy. Some rows may be miscategorised — please re-run.
      </div>
    );
  }
  // Normal — small age line if data is >24h old.
  if (lastSynced) {
    const ageMs = Date.now() - new Date(lastSynced).getTime();
    if (ageMs > 24 * 60 * 60 * 1000) {
      return (
        <div className={`${styles.banner} ${styles.bannerAge}`}>
          Last synced {fmtIST(lastSynced)} — data is over 24 h old.
        </div>
      );
    }
  }
  return null;
};

const SummaryStrip = ({ summary, onFilterClick }) => {
  if (!summary) return null;
  const {
    total_rows,
    avg_score,
    with_missing_mac,
    suppressed_states = {},
    health_flags = {},
    at_risk_count = 0,
    reason_unavailable_count = 0,
    at_risk_totals = null,
  } = summary;
  // #11 Each tile can drill into a specific filter. Prop wired from parent.
  const item = (label, value, hint, filterKey) => (
    <div
      className={`${styles.summaryItem} ${filterKey ? styles.summaryItemClickable : ''}`}
      onClick={filterKey ? () => onFilterClick(filterKey) : undefined}
      role={filterKey ? 'button' : undefined}
      tabIndex={filterKey ? 0 : undefined}
    >
      <div className={styles.summaryLabel}>{label}</div>
      <div className={styles.summaryValue}>{value ?? '—'}</div>
      {hint && <div className={styles.summaryHint}>{hint}</div>}
    </div>
  );
  const suppressedCount =
    (suppressed_states.search_suppressed || 0)
    + (suppressed_states.listing_suppressed || 0)
    + (suppressed_states.attribute_suppressed || 0)
    + (suppressed_states.catalog_item_removed || 0);
  return (
    <div className={styles.summaryStrip}>
      {item('Rows', total_rows, null, 'all')}
      {item('Avg. completeness', avg_score != null ? `${avg_score}/100` : '—')}
      {item('Missing attrs', with_missing_mac, null, 'with_missing')}
      {item('Suppressed', suppressedCount, 'across all suppression types', 'suppressed')}
      {item('At risk', at_risk_count, 'blocking issue, not yet suppressed', 'at_risk')}
      {item('With warnings', health_flags.has_warnings || 0)}
      {item('Low score', health_flags.low_score || 0, 'below 60')}
      {reason_unavailable_count > 0 && item(
        'Reason unavailable', reason_unavailable_count,
        'suppressed but Amazon returned no reason')}
      {/* #65 what listings that can't sell are costing */}
      {at_risk_totals && item(
        "Can't sell", at_risk_totals.cant_sell_skus,
        `₹${Math.round(at_risk_totals.revenue_at_risk_30d || 0).toLocaleString('en-IN')}/month revenue at risk`,
        'suppressed')}
      {at_risk_totals && at_risk_totals.inventory_at_risk_units > 0 && item(
        'Stock stuck', `${at_risk_totals.inventory_at_risk_units} units`,
        at_risk_totals.inventory_at_risk_value_minor != null
          ? `₹${Math.round(at_risk_totals.inventory_at_risk_value_minor / 100).toLocaleString('en-IN')} at cost`
            + (at_risk_totals.skus_with_cost < at_risk_totals.skus_with_stuck_stock
              ? ` (${at_risk_totals.skus_with_cost} of ${at_risk_totals.skus_with_stuck_stock} SKUs costed)` : '')
          : 'no cost entered in Profit & Margin')}
    </div>
  );
};

// #16 Time-left badge — colour by urgency. Days in single digits are red;
// under two weeks amber; further out grey. Null / no enforcement → nothing.
const TimeLeftBadge = ({ days, enforceBy }) => {
  if (days == null) return null;
  let cls = styles.timeLeftFar;
  if (days <= 7) cls = styles.timeLeftUrgent;
  else if (days <= 14) cls = styles.timeLeftSoon;
  const label = days < 0
    ? `${Math.abs(days)}d overdue`
    : days === 0 ? 'today' : `${days}d left`;
  const title = enforceBy ? `Enforces ${enforceBy}` : undefined;
  return <span className={`${styles.timeLeft} ${cls}`} title={title}>{label}</span>;
};

const AsinDrawer = ({ asin, onClose }) => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAsinDetail(authCtx.token, asin);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [asin, authCtx.token]);

  const header = state.data?.header || {};
  const revenue = state.data?.revenue || {};
  return (
    <div className={styles.drawer} role="dialog" aria-modal="true">
      <div className={styles.drawerHeader}>
        {/* #35 Header: MAIN thumbnail on the left, title + ASIN + SKU count on
             the right. Thumb URL comes from images collection; title from the
             first non-null across sibling SKUs. */}
        <div className={styles.drawerHeaderMain}>
          {header.main_image_url ? (
            <img
              src={header.main_image_url}
              alt=""
              className={styles.drawerThumb}
              loading="lazy"
            />
          ) : (
            <div className={`${styles.drawerThumb} ${styles.drawerThumbPlaceholder}`}>—</div>
          )}
          <div>
            <div className={styles.drawerTitle}>
              {header.title || `ASIN ${asin}`}
            </div>
            {state.data && (
              <div className={styles.drawerSubtitle}>
                {header.title ? `ASIN ${asin} · ` : ''}
                {state.data.sku_count} SKU{state.data.sku_count === 1 ? '' : 's'} · captured{' '}
                {fmtIST(state.data.skus?.[0]?.captured_at) || fmtIST(new Date().toISOString())}
                {revenue.total_30d > 0 && (
                  <> · {revenue.currency} {Math.round(revenue.total_30d).toLocaleString()} / 30d</>
                )}
              </div>
            )}
          </div>
        </div>
        <button className={styles.drawerClose} onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div className={styles.drawerBody}>
        {state.loading && <div className={styles.muted}>Loading…</div>}
        {state.error && <div className={styles.error}>{state.error}</div>}
        {state.data && (
          <>
            <section>
              <h4>SKUs on this ASIN</h4>
              <table className={styles.subTable}>
                <thead>
                  <tr>
                    <th>SKU</th><th>Type</th><th>Score</th><th>MAC missing</th>
                    <th>Suppression</th><th>Findings</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.skus.map((s) => (
                    <tr key={s.sku}>
                      <td className={styles.mono}>{s.sku}</td>
                      <td>{s.product_type || '—'}</td>
                      <td>{s.score != null ? s.score : <span className={styles.muted}>Not scored</span>}</td>
                      <td>
                        {s.mac.missing.length === 0
                          ? <span className={styles.muted}>—</span>
                          : s.mac.missing.slice(0, 3).join(', ')
                            + (s.mac.missing.length > 3 ? ` +${s.mac.missing.length - 3}` : '')}
                      </td>
                      <td><StatePill state={s.suppression.state} /></td>
                      <td>{s.suppression.findings_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section>
              <h4>Images ({state.data.images.variants?.length || 0} variants)</h4>
              {state.data.images.variants?.length ? (
                <ul className={styles.chipList}>
                  {state.data.images.variants.map((v) => (
                    <li key={v.variant} className={styles.imgChip}>
                      <span className={styles.chipTitle}>{v.variant}</span>
                      <span className={styles.chipMeta}>
                        {v.longest_px}×{v.shortest_px}px
                        {v.confirmed ? ' · ✓ source confirmed' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={styles.muted}>No images for this ASIN.</div>
              )}
              {state.data.images.is_variation_child && (
                <div className={styles.hint}>Variation child — swatch image expected.</div>
              )}
            </section>

            {state.data.change_events?.length > 0 && (
              <section>
                <h4>Recent image changes ({state.data.change_events.length})</h4>
                <ul className={styles.eventList}>
                  {state.data.change_events.slice(0, 10).map((ev, i) => (
                    <li key={i}>
                      <b>{ev.variant}</b> changed on{' '}
                      {fmtIST(ev.detected_at)}
                      {ev.attributed_to === 'unknown' && (
                        <span className={styles.hint}> · attribution pending</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h4>Fix queue ({state.data.fix_queue?.length || 0})</h4>
              {state.data.fix_queue?.length ? (
                <ol className={styles.fixList}>
                  {state.data.fix_queue.slice(0, 20).map((f, i) => (
                    <li key={i} className={styles[`fix_${f.remediable_via}`] || styles.fixDefault}>
                      <div className={styles.fixLine1}>
                        {/* #37 severity tier — visible chip before the gap type */}
                        {f.tier && (
                          <span className={`${styles.tierChip} ${styles[`tier_${f.tier}`] || ''}`}>
                            {f.tier.replace('_', ' ')}
                          </span>
                        )}
                        <span className={styles.fixType}>{f.type.replace(/_/g, ' ')}</span>
                        {/* #39 content vs offer tag — who owns the fix */}
                        {f.fix_scope && (
                          <span className={`${styles.scopeChip} ${styles[`scope_${f.fix_scope}`] || ''}`}>
                            {f.fix_scope}
                          </span>
                        )}
                        {f.sku && <span className={styles.fixSku}>· {f.sku}</span>}
                        {f.set && <span className={styles.fixSet}>· {f.set}</span>}
                      </div>
                      <div className={styles.fixLine2}>
                        {f.attribute && <span className={styles.mono}>{f.attribute}</span>}
                        {f.reason_code && <span className={styles.mono}>{f.reason_code}</span>}
                        {f.message && <span className={styles.fixMsg}>{f.message}</span>}
                      </div>
                      <div className={styles.fixRoute}>
                        via {f.remediable_via}
                        {/* #38 impact — only render when non-zero */}
                        {f.revenue_at_risk > 0 && (
                          <span className={styles.fixImpact}>
                            · ~{revenue.currency || 'INR'} {Math.round(f.revenue_at_risk).toLocaleString()} at risk
                          </span>
                        )}
                      </div>
                      {/* #45 Manual instructions for manual_case findings.
                          Server chooses copy by reason_code. */}
                      {f.manual_instructions?.length > 0 && (
                        <ol className={styles.manualSteps}>
                          {f.manual_instructions.map((step, si) => (
                            <li key={si}>{step}</li>
                          ))}
                        </ol>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <div className={styles.muted}>No open fixes.</div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const EnrichedRowsPanel = () => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, rows: [], summary: {}, error: null });
  const [sort, setSort] = useState('score_asc');
  const [filter, setFilter] = useState('all');
  const [fulfillment, setFulfillment] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [openAsin, setOpenAsin] = useState(null);

  // #61 debounce the search input — 250 ms is long enough to skip mid-typing
  // fetches, short enough to feel responsive.
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchRows = useCallback(async () => {
    if (!authCtx?.token) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { rows, summary } = await getCatalogueRows(authCtx.token, {
        sort, filter, fulfillment, q, limit: 200,
      });
      setState({ loading: false, rows, summary, error: null });
    } catch (e) {
      setState({ loading: false, rows: [], summary: {}, error: e.message });
    }
  }, [authCtx?.token, sort, filter, fulfillment, q]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  if (!authCtx?.token) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h3 className={styles.panelTitle}>Catalogue Health · Enriched view</h3>
          <div className={styles.panelSubtitle}>
            Feature-vector-backed. Reads persisted schema diff + suppressions + images.
          </div>
        </div>
        <div className={styles.panelControls}>
          {/* #61 Search — SKU / ASIN / title, case-insensitive. Debounced 250 ms. */}
          <input
            type="search"
            placeholder="Search SKU / ASIN / title"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            {FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* #62 Fulfillment channel filter */}
          <select value={fulfillment} onChange={(e) => setFulfillment(e.target.value)}>
            {FULFILLMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={fetchRows} disabled={state.loading}>
            {state.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <DataStateBanner summary={state.summary} />
      <SummaryStrip summary={state.summary} onFilterClick={setFilter} />

      {state.error && <div className={styles.error}>Error: {state.error}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.rowsTable}>
          <thead>
            <tr>
              <th />{/* #35 thumbnail column — width kept tight to not push
                     the rest of the table around */}
              <th>Product</th>{/* SKU + ASIN + title */}
              <th>Type</th>
              <th title="0-100 Listing Completeness: weighted mix of MAC, RAC, images, title. A+ deferred to v1.">Completeness</th>
              <th title="Mandatory attributes — Amazon actively blocks the listing on these">MAC</th>
              <th title="Required attributes — Amazon labels required but tolerates">RAC</th>
              <th title="MAC missing, RAC missing, and image issues as distinct chips">Missing</th>
              <th>Images</th>
              <th>Suppression</th>
              <th>Top action</th>
              <th title="30-day revenue on this ASIN scaled by suppression state">At risk</th>
              <th>Flags</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {state.loading && state.rows.length === 0 && (
              <tr><td colSpan={13} className={styles.muted}>Loading rows…</td></tr>
            )}
            {!state.loading && state.rows.length === 0 && (
              <tr><td colSpan={13} className={styles.muted}>
                No rows yet — trigger a Catalogue Health refresh, then reload.
              </td></tr>
            )}
            {state.rows.map((r) => {
              const macPct = r.mac_ratio != null ? Math.round(r.mac_ratio * 100) : null;
              const racPct = r.rac_ratio != null ? Math.round(r.rac_ratio * 100) : null;
              /* #32 — MAIN image missing is a hard blocker like a MAC
                 attribute. Use `hard_missing` (mac_missing + optional
                 'main_product_image') for the "Missing" chip so it counts
                 uniformly. The MAC column below keeps the raw schema ratio
                 so the fraction stays truthful. */
              const macMissing = r.mac_missing || [];
              const hardMissing = r.hard_missing || macMissing;
              const racMissing = r.rac_missing || [];
              const imgUnknown = r.image_component_ratio == null;
              const imgPct = imgUnknown ? null : Math.round(r.image_component_ratio * 100);
              // Compose image "issues" as distinct chips so both attribute and
              // image defects surface in the Missing column. Per DRD §3 they
              // stay separate CATEGORIES — that's why they're distinctly
              // styled chips, not folded into a single count.
              const imageIssues = [];
              if (!imgUnknown) {
                if (r.image_count === 0) imageIssues.push('no images');
                else if (r.image_count < 5) imageIssues.push(`only ${r.image_count} images`);
                /* #32 "no MAIN" no longer shown here — it's folded into the
                   MAC chip via hardMissing so the seller sees one blocker
                   count, not two separate ones for the same problem. */
                if ((r.image_zoom_unconfirmed || []).length > 0) {
                  imageIssues.push(`${r.image_zoom_unconfirmed.length} zoom unconfirmed`);
                }
              }
              const nothingMissing = hardMissing.length === 0
                && racMissing.length === 0
                && imageIssues.length === 0;
              return (
                <tr key={r.sku} onClick={() => setOpenAsin(r.asin)} className={styles.clickable}>
                  {/* #35 thumbnail */}
                  <td>
                    {r.main_image_url ? (
                      <img
                        src={r.main_image_url}
                        alt=""
                        className={styles.rowThumb}
                        loading="lazy"
                      />
                    ) : (
                      <div className={`${styles.rowThumb} ${styles.rowThumbPlaceholder}`} />
                    )}
                  </td>
                  {/* Combined product cell — title stacked above SKU/ASIN,
                       fulfillment chip next to the IDs (#66). */}
                  <td>
                    {r.title && (
                      <div className={styles.rowTitle} title={r.title}>
                        {r.title.length > 60 ? r.title.slice(0, 60) + '…' : r.title}
                      </div>
                    )}
                    <div className={styles.rowIds}>
                      <span className={styles.mono}>{r.sku}</span>
                      {r.asin && <span className={styles.mono}>· {r.asin}</span>}
                      {r.fulfillment_type && (
                        <span className={`${styles.channelChip} ${styles[`channel_${r.fulfillment_type}`] || ''}`}>
                          {r.fulfillment_type}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{r.product_type || '—'}</td>
                  <td>
                    {r.product_type_unknown ? (
                      /* #30 — Amazon returned no productType; nothing to check */
                      <span className={styles.cannotCheck} title="Amazon has not classified this listing into a product type — completeness checks not possible.">Cannot be checked</span>
                    ) : r.score == null ? (
                      <span className={styles.muted}>Not scored</span>
                    ) : (
                      <div className={styles.scoreCell}>
                        <div className={styles.scoreVal}>{r.score.toFixed(1)}</div>
                        <div className={styles.scoreBar}>
                          <div
                            className={scoreBarClass(r.score)}
                            style={{ width: `${Math.max(0, Math.min(100, r.score))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {macPct == null ? <span className={styles.muted}>—</span> : (
                      <div>
                        <div>{macPct}%</div>
                        <div className={styles.mono}>
                          {(r.mac_required_total - macMissing.length)}/{r.mac_required_total}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {r.rac_required_total === 0 ? (
                      <span className={styles.muted} title="Amazon reports no soft-required attributes for this product type">n/a</span>
                    ) : racPct == null ? '—' : (
                      <div>
                        <div>{racPct}%</div>
                        <div className={styles.mono}>
                          {(r.rac_required_total - racMissing.length)}/{r.rac_required_total}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {nothingMissing ? (
                      <span className={styles.muted}>—</span>
                    ) : (
                      <div className={styles.missingChips}>
                        {hardMissing.length > 0 && (
                          <div className={`${styles.missingGroup} ${styles.missingGroupMac}`}
                               title={hardMissing.join(', ')}>
                            <span className={styles.missingGroupLabel}>MAC</span>
                            <span className={styles.missingGroupCount}>{hardMissing.length}</span>
                            <span className={styles.mono}>
                              {hardMissing.slice(0, 2).join(', ')}
                              {hardMissing.length > 2 ? ` +${hardMissing.length - 2}` : ''}
                            </span>
                          </div>
                        )}
                        {racMissing.length > 0 && (
                          <div className={`${styles.missingGroup} ${styles.missingGroupRac}`}
                               title={racMissing.join(', ')}>
                            <span className={styles.missingGroupLabel}>RAC</span>
                            <span className={styles.missingGroupCount}>{racMissing.length}</span>
                            <span className={styles.mono}>
                              {racMissing.slice(0, 2).join(', ')}
                              {racMissing.length > 2 ? ` +${racMissing.length - 2}` : ''}
                            </span>
                          </div>
                        )}
                        {imageIssues.map((issue, i) => (
                          <div key={i} className={`${styles.missingGroup} ${styles.missingGroupImage}`}>
                            <span className={styles.missingGroupLabel}>IMG</span>
                            <span>{issue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {r.image_count === 0 ? (
                      <span className={styles.warn}>0 images</span>
                    ) : imgUnknown ? (
                      <span className={styles.muted}>?</span>
                    ) : (
                      <div>
                        <div>{r.image_count} variants</div>
                        <div className={styles.mono}>{imgPct}%</div>
                      </div>
                    )}
                  </td>
                  <td>
                    <StatePill state={r.suppression_state} />
                    {/* #15/17 Action-points count: distinct fixable problems,
                        NOT the raw finding count. Suppressed. */}
                    {r.distinct_action_points > 0 && (
                      <div className={styles.actionPoints}>
                        {r.distinct_action_points} action{r.distinct_action_points === 1 ? '' : 's'}
                      </div>
                    )}
                    {/* #16 Time-left badge under state pill for at-a-glance urgency */}
                    <TimeLeftBadge days={r.time_left_days} enforceBy={r.earliest_enforce_by} />
                  </td>
                  <td>
                    {r.reason_unavailable ? (
                      /* #22 Suppressed but Amazon returned no reason on either
                         source. Explicit label so the seller knows to open a case. */
                      <div>
                        <div className={styles.actionCode}>Reason unavailable</div>
                        <div className={styles.actionRoute}>Contact Seller Support</div>
                      </div>
                    ) : r.top_suppression ? (
                      <div>
                        <div className={styles.actionCode}>{r.top_suppression.reason_code}</div>
                        <div className={styles.actionRoute}>via {r.top_suppression.remediable_via}</div>
                      </div>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  {/* #38 revenue-at-risk + #65 inventory-at-risk stacked */}
                  <td>
                    {r.revenue_at_risk_30d > 0 || r.inventory_at_risk > 0 ? (
                      <div>
                        {r.revenue_at_risk_30d > 0 && (
                          <div className={styles.riskAmt}>
                            {r.revenue_currency} {Math.round(r.revenue_at_risk_30d).toLocaleString()}
                          </div>
                        )}
                        {r.inventory_at_risk > 0 && (
                          <div className={styles.riskInv}>
                            {r.inventory_at_risk} unit{r.inventory_at_risk === 1 ? '' : 's'} stuck
                          </div>
                        )}
                        {r.asin_revenue_30d > 0 && (
                          <div className={styles.riskFraction}>
                            of {Math.round(r.asin_revenue_30d).toLocaleString()} / 30d
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td><HealthFlags flags={r.health_flags} /></td>
                  <td className={styles.chevron}>›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openAsin && (
        <div className={styles.overlay} onClick={() => setOpenAsin(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <AsinDrawer asin={openAsin} onClose={() => setOpenAsin(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichedRowsPanel;
